import React, { useState, FC } from 'react';
import { CRMTemplateView } from 'common';
import { ActionButton } from '@src/components/ActionButton';
import { OverlayContainer } from '@src/components/OverlayContainer';
import { CloseButton } from '@src/components/CloseButton';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { getCrmTemplates } from '@src/queries/crmtemplates/api';
import { useAtomValue } from 'jotai';
import { EPopup } from '@src/types/common';
import { TableFilter } from '@src/components/TableFilter';
import { Loader } from '@src/components/Loader';
import { popupsAtom, setPopup as atomSetPopup } from '@src/state/popupAtoms';

interface TemplateTableProps {
  templates: CRMTemplateView[];
  loading: boolean;
  selectedTemplateId: string | null;
  onSelectTemplate: (template: CRMTemplateView) => void;
  getFilterOptions: (field: string) => Array<{ value: string; label: string; selected: boolean; }>;
  onFilterChange: (field: string, selected: string[]) => void;
  onSortChange: (sortBy: string, direction: 'asc' | 'desc') => void;
  currentSortBy: string;
  currentSortDirection: 'asc' | 'desc';
  filterType?: string | null;
}

const TemplateTable: FC<TemplateTableProps> = ({
  templates,
  loading,
  selectedTemplateId,
  onSelectTemplate,
  getFilterOptions,
  onFilterChange,
  onSortChange,
  currentSortBy,
  currentSortDirection,
  filterType,
}) => {
  const tableClass = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: 16,
    marginBottom: 16,
    fontSize: 14,
    background: 'transparent',
    color: 'white',
  };

  const headerRowClass = {
    borderBottom: '2px solid #444',
    background: 'transparent',
  };

  const thClass = {
    fontWeight: 600,
    fontSize: 15,
    color: 'white',
    textAlign: 'left' as const,
    padding: '14px 8px',
    background: 'transparent',
  };

  const headerWithFilterClass = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 20,
  };

  const rowClass = {
    borderBottom: '1px solid #333',
    background: 'transparent',
    transition: 'background 0.15s',
    height: 54,
  };

  const tdClass = {
    color: 'white',
    padding: '8px',
    fontSize: 14,
    verticalAlign: 'middle' as const,
    height: 54,
    maxWidth: 150,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  };

  const actionColClass = {
    width: 120,
    minWidth: 120,
    textAlign: 'center' as const,
    padding: '8px',
  };

  const sortIconClass = { fontSize: '12px', cursor: 'pointer' }
  
  return (
    <table style={tableClass}>
      <thead>
        <tr style={headerRowClass}>
          <th style={actionColClass}>Action</th>
          <th style={thClass}>
            <div style={headerWithFilterClass}>
              Date
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {currentSortBy === 'date' ? (
                  currentSortDirection === 'asc' ? 
                    <span style={sortIconClass} onClick={() => onSortChange('date', 'desc')}>↑</span> :
                    <span style={sortIconClass} onClick={() => onSortChange('date', 'asc')}>↓</span>
                ) : null }
                {currentSortBy !== 'date' ? (
                  <span style={{ fontSize: '14px', color: '#888', cursor: 'pointer' }} onClick={() => onSortChange('date', 'desc')}>↕</span>
                ) : null}
              </div>
            </div>
          </th>
          <th style={thClass}>
            <div style={headerWithFilterClass}>
              Campaign
              <TableFilter
                type="filter"
                options={getFilterOptions('campaign')}
                onFilterChange={(selected) => onFilterChange('campaign', selected)}
              />
            </div>
          </th>
          <th style={thClass}>
            <div style={headerWithFilterClass}>
              Market
              <TableFilter
                type="filter"
                options={getFilterOptions('market')}
                onFilterChange={(selected) => onFilterChange('market', selected)}
              />
            </div>
          </th>
          <th style={thClass}>
            <div style={headerWithFilterClass}>
              Language
              <TableFilter
                type="filter"
                options={getFilterOptions('language')}
                onFilterChange={(selected) => onFilterChange('language', selected)}
              />
            </div>
          </th>
          {filterType !== 'DM' ? (
            <th style={thClass}>
              <div style={headerWithFilterClass}>
                Hashtag
                <TableFilter
                  type="filter"
                  options={getFilterOptions('hashtag')}
                  onFilterChange={(selected) => onFilterChange('hashtag', selected)}
                />
              </div>
            </th>
          ) : null }
          <th style={thClass}>Content</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={filterType === 'DM' ? 6 : 7} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Loader size={20} />
                <span>Loading templates...</span>
              </div>
            </td>
          </tr>
        ) : templates.length === 0 ? (
          <tr>
            <td colSpan={filterType === 'DM' ? 6 : 7} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
              No templates found
            </td>
          </tr>
        ) : (
          templates.map((template, idx) => {
            const isSelected = selectedTemplateId === template._id;
            return (
              <tr key={idx} style={rowClass}>
                <td style={actionColClass}>
                  <ActionButton
                    variant={isSelected ? 'blue' : 'transparent'}
                    size="small"
                    onAction={() => onSelectTemplate(template)}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </ActionButton>
                </td>
                <td style={tdClass}>
                  {template.createdAt ? new Date(template.createdAt).toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: '2-digit' 
                  }) : ''}
                </td>
                <td style={tdClass}>{template.name}</td>
                <td style={tdClass}>{template.market}</td>
                <td style={tdClass}>{template.language}</td>
                {filterType !== 'DM' ? (
                  <td style={tdClass}>
                    {template.type === 'RR' ? (template.hashtag || '') : <span style={{ color: '#888' }}>N/A</span>}
                  </td>
                ) : null}
                <td style={{ ...tdClass, maxWidth: 300 }}>{template.content}</td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: CRMTemplateView) => void;
  selectedTemplateId?: string | null;
  filterType?: 'RR' | 'DM' | null; // Filter templates by type
}

export const TemplateSelector: FC<TemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  selectedTemplateId = null,
  filterType = null,
}) => {
  const [templates, setTemplates] = useState<CRMTemplateView[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<CRMTemplateView[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Filter and sort state
  const [filters, setFilters] = useState({
    campaign: [] as string[],
    hashtag: [] as string[],
    market: [] as string[],
    language: [] as string[],
    type: [] as string[],
  });
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const popups = useAtomValue(popupsAtom);
  const templateSelectorOpen = popups[EPopup.templateSelector];
  const crmSettingsOpen = popups[EPopup.crmSettings];

  // Get filterType from atom if not provided as prop
  const effectiveFilterType = filterType || (typeof templateSelectorOpen === 'object' ? templateSelectorOpen?.filterType : null) || null;

  React.useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Reload templates when CRM settings is closed (indicating possible changes)
  React.useEffect(() => {
    if (isOpen && !crmSettingsOpen) {
      loadTemplates();
    }
  }, [crmSettingsOpen, isOpen]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await getCrmTemplates();
      let filteredTemplates = result || [];
      
      // Filter by type if specified
      if (effectiveFilterType) {
        filteredTemplates = filteredTemplates.filter(template => template.type === effectiveFilterType);
      }
      
      // Sort by creation date (newest first) by default
      filteredTemplates.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      
      setTemplates(filteredTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and sorting
  React.useEffect(() => {
    let filtered = [...templates];

    // Apply filters
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter(template => {
          switch (key) {
            case 'campaign':
              return values.includes(template.name || '');
            case 'hashtag':
              return values.includes(template.hashtag || '');
            case 'market':
              return values.includes(template.market || '');
            case 'language':
              return values.includes(template.language || '');
            case 'type':
              return values.includes(template.type);
            default:
              return true;
          }
        });
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.createdAt);
          valueB = new Date(b.createdAt);
          break;
        case 'campaign':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'hashtag':
          valueA = (a.hashtag || '').toLowerCase();
          valueB = (b.hashtag || '').toLowerCase();
          break;
        case 'market':
          valueA = (a.market || '').toLowerCase();
          valueB = (b.market || '').toLowerCase();
          break;
        case 'language':
          valueA = (a.language || '').toLowerCase();
          valueB = (b.language || '').toLowerCase();
          break;
        case 'type':
          valueA = a.type.toLowerCase();
          valueB = b.type.toLowerCase();
          break;
        default:
          valueA = new Date(a.createdAt);
          valueB = new Date(b.createdAt);
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTemplates(filtered);
  }, [templates, filters, sortBy, sortDirection]);

  // Generate filter options from templates
  const getFilterOptions = (field: string) => {
    const values = new Set<string>();
    templates.forEach(template => {
      let value = '';
      switch (field) {
        case 'campaign':
          value = template.name || '';
          break;
        case 'hashtag':
          value = template.hashtag || '';
          break;
        case 'market':
          value = template.market || '';
          break;
        case 'language':
          value = template.language || '';
          break;
        case 'type':
          value = template.type;
          break;
      }
      if (value) values.add(value);
    });
    
    return Array.from(values).sort().map(value => ({
      value,
      label: value,
      selected: filters[field as keyof typeof filters].includes(value),
    }));
  };

  const handleFilterChange = (field: string, selected: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: selected,
    }));
  };

  const handleSortChange = (newSortBy: string, direction: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(direction);
  };

  const openCrmSettings = () => {
    atomSetPopup(EPopup.crmSettings, true);
  };

  if (!isOpen) return null;

  return (
    <OverlayContainer
      containerClasses={['template-selector-container']}
      frameClasses={['template-selector-frame']}
      onClose={onClose}
      style={{ zIndex: 1600 }}
    >
      <div style={{ 
        padding: '20px', 
        minWidth: '800px', 
        maxWidth: '1200px',
        width: '90vw',
        height: '80vh',
        backgroundColor: '#1b1b1b',
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          borderBottom: '1px solid #333',
          paddingBottom: '15px'
        }}>
          <h2 style={{ 
            color: 'white', 
            margin: 0, 
            fontSize: '24px',
            fontWeight: 600 
          }}>
            {effectiveFilterType === 'RR' ? 'Select Rights Request Template' : 'Select Template'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ActionButton
              variant="transparent"
              size="small"
              onAction={openCrmSettings}
            >
              Manage Templates
            </ActionButton>
            <CloseButton size="large" onClick={onClose} />
          </div>
        </div>
        <PerfectScrollbar style={{ 
          height: 'calc(100% - 70px)',
          backgroundColor: 'transparent'
        }}>
          <TemplateTable
            templates={filteredTemplates}
            loading={loading}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={onSelectTemplate}
            getFilterOptions={getFilterOptions}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            currentSortBy={sortBy}
            currentSortDirection={sortDirection}
            filterType={effectiveFilterType}
          />
        </PerfectScrollbar>
      </div>
    </OverlayContainer>
  );
};

export default TemplateSelector;
